import { Link, useNavigate } from "react-router-dom";
import { Heart, Rocket, ShieldCheck } from "lucide-react";
import { imgUrl, formatPrice, CONDITIONS, api, getFees, withProtection, trackAd } from "../lib/api";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BadgeChip } from "./BadgeChip";

export const ItemCard = ({ item, index = 0 }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [fav, setFav] = useState(item.is_favorite || false);
  const [favCount, setFavCount] = useState(item.favorites_count || 0);
  const [fees, setFees] = useState(null);
  const img = item.image_ids?.[0];

  useEffect(() => { getFees().then(setFees); }, []);
  useEffect(() => { if (item.is_boosted) trackAd(item.id, "impression"); }, [item.id, item.is_boosted]);
  const protectedPrice = withProtection(item.price, fees);

  const toggleFav = async (e) => {
    e.preventDefault();
    if (!user) return nav("/anmelden");
    try {
      const { data } = await api.post(`/favorites/${item.id}`);
      setFav(data.favorite);
      setFavCount((c) => Math.max(0, c + (data.favorite ? 1 : -1)));
      toast.success(data.favorite ? "Zu Favoriten hinzugefügt" : "Aus Favoriten entfernt");
    } catch {}
  };

  return (
    <Link to={`/artikel/${item.id}`} data-testid={`item-card-${item.id}`}
      onClick={() => item.is_boosted && trackAd(item.id, "click")}
      className="group block fade-up"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}>
      <div className="relative aspect-[2/3] bg-muted overflow-hidden rounded-xl">
        {img ? (
          <img src={imgUrl(img, "medium")} alt={item.title} loading={index < 4 ? "eager" : "lazy"}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Kein Bild</div>
        )}
        <button onClick={toggleFav} data-testid={`fav-btn-${item.id}`} aria-label="Favorit"
          className="absolute bottom-2.5 right-2.5 bg-white/95 shadow-sm rounded-full px-2.5 py-1.5 flex items-center gap-1 hover:scale-105 active:scale-95 transition-transform">
          <Heart className={`w-4 h-4 ${fav ? "fill-accent text-accent" : "text-foreground"}`} />
          {favCount > 0 && <span className="text-xs font-semibold">{favCount}</span>}
        </button>
        {item.is_boosted && (
          <span data-testid={`boost-badge-${item.id}`} className="absolute top-2.5 right-2.5 bg-white/95 text-accent text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm">
            <Rocket className="w-3 h-3" /> Boost
          </span>
        )}
        {item.status !== "ACTIVE" && (
          <span className="absolute top-2.5 left-2.5 bg-foreground/85 text-white text-xs font-bold px-2.5 py-1 rounded-md" data-testid={`item-status-${item.id}`}>
            {item.status === "SOLD" ? "Verkauft" : item.status === "RESERVED" ? "Reserviert" : item.status}
          </span>
        )}
      </div>
      <div className="pt-2 px-0.5">
        <p className="text-sm text-muted-foreground truncate">{item.brand || item.title}</p>
        <p className="text-sm text-muted-foreground truncate">{item.size ? `Gr. ${item.size} · ` : ""}{CONDITIONS[item.condition] || item.condition}</p>
        <p className="text-sm text-foreground/80 mt-1.5">{formatPrice(item.price)}</p>
        {protectedPrice != null && (
          <p className="text-sm text-accent font-semibold mt-0.5 flex items-center gap-1" data-testid={`protected-price-${item.id}`}>
            {formatPrice(protectedPrice)} inkl. <ShieldCheck className="w-3.5 h-3.5" />
          </p>
        )}
        {item.authenticity_status === "AUTHENTICITY_VERIFIED" && (
          <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-accent border border-accent/30 bg-accent/5 rounded-full px-2 py-0.5">KLEDIO geprüft ✓</span>
        )}
        {item.seller?.badges?.[0] && <div className="mt-1.5"><BadgeChip badge={item.seller.badges[0]} /></div>}
      </div>
    </Link>
  );
};
